import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, auth } from "@/lib/api";
import EntryCard from "@/components/EntryCard";
import EntryModal from "@/components/EntryModal";
import Confirm from "@/components/Confirm";
import Toast from "@/components/Toast";
import { classifyHosting } from "@/lib/hosting";
import {
  TOAST_DURATION_MS,
  SEARCH_DEBOUNCE_MS,
  ENTRY_TYPES,
  ENTRY_STATUSES,
  SORT_OPTIONS,
  HOSTING,
  HOSTING_FILTER_OPTIONS,
} from "@/lib/constants";

function computeStats(entries) {
  let live = 0;
  let work = 0;
  let own = 0;
  let emergent = 0;
  let noUrl = 0;
  for (const entry of entries) {
    if (entry.status === "Live") live++;
    else if (entry.status === "In Arbeit") work++;
    const h = classifyHosting(entry.url);
    if (h === HOSTING.OWN) own++;
    else if (h === HOSTING.EMERGENT) emergent++;
    else noUrl++;
  }
  return { total: entries.length, live, work, own, emergent, noUrl };
}

function buildEntriesQuery({ search, type, status, sort }) {
  const params = {};
  if (search) params.search = search;
  if (type) params.type = type;
  if (status) params.status = status;
  if (sort) params.sort = sort;
  return params;
}

function renderEmptyText(hasFilters) {
  if (hasFilters) return "Keine Treffer für deine Filter.";
  return "Klick auf „Neuer Eintrag“ und trag deine erste URL ein.";
}

export default function Dashboard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [hostingFilter, setHostingFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  const showToast = useCallback((msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = buildEntriesQuery({
        search,
        type: typeFilter,
        status: statusFilter,
        sort,
      });
      const { data } = await api.get("/entries", { params });
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      showToast("Fehler beim Laden", "err");
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter, sort, showToast]);

  // Debounce filter / search / sort changes
  useEffect(() => {
    const t = setTimeout(() => load(), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [load]);

  const stats = useMemo(() => computeStats(entries), [entries]);

  // Apply hosting filter client-side (derived from URL, no backend filter)
  const visibleEntries = useMemo(() => {
    if (!hostingFilter) return entries;
    return entries.filter((e) => classifyHosting(e.url) === hostingFilter);
  }, [entries, hostingFilter]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (entry) => {
    setEditing(entry);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (payload) => {
    try {
      if (editing?.id) {
        const { data } = await api.put(`/entries/${editing.id}`, payload);
        setEntries((prev) => prev.map((x) => (x.id === data.id ? data : x)));
        showToast("Eintrag aktualisiert");
      } else {
        const { data } = await api.post("/entries", payload);
        setEntries((prev) => [data, ...prev]);
        showToast("Eintrag angelegt");
      }
      closeModal();
      load();
    } catch {
      showToast("Speichern fehlgeschlagen", "err");
    }
  };

  const askDelete = (entry) => {
    setConfirm({
      title: "Eintrag löschen?",
      message: `„${entry.name || entry.url || "Ohne Namen"}“ wird unwiderruflich gelöscht.`,
      confirmLabel: "Löschen",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/entries/${entry.id}`);
          setEntries((prev) => prev.filter((x) => x.id !== entry.id));
          showToast("Eintrag gelöscht");
        } catch {
          showToast("Löschen fehlgeschlagen", "err");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      const { data } = await api.get("/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `url-zentrale-backup-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Backup heruntergeladen");
    } catch {
      showToast("Export fehlgeschlagen", "err");
    }
  };

  const handleImportClick = () => fileRef.current?.click();

  const restoreEntries = async (items, mode) => {
    try {
      await api.post("/restore", { entries: items, mode });
      const verb = mode === "replace" ? "importiert (ersetzt)" : "zusammengeführt";
      showToast(`${items.length} Einträge ${verb}`);
      load();
    } catch {
      showToast("Import fehlgeschlagen", "err");
    } finally {
      setConfirm(null);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const items = Array.isArray(json) ? json : json.entries;
      if (!Array.isArray(items)) throw new Error("Ungültiges Format");
      setConfirm({
        title: "Backup importieren?",
        message: `${items.length} Einträge gefunden. „Ersetzen“ löscht aktuelle Daten und ersetzt sie. „Zusammenführen“ fügt hinzu / aktualisiert.`,
        confirmLabel: "Ersetzen",
        secondaryLabel: "Zusammenführen",
        danger: true,
        onConfirm: () => restoreEntries(items, "replace"),
        onSecondary: () => restoreEntries(items, "merge"),
      });
    } catch {
      showToast("Datei konnte nicht gelesen werden", "err");
    }
  };

  const handleLogout = () => {
    auth.logout();
    window.location.href = "/login";
  };

  const hasFilters = !!(search || typeFilter || statusFilter || hostingFilter);

  let listSection;
  if (loading) {
    listSection = (
      <div className="empty">
        <h2>Lädt …</h2>
        <p>Einträge werden geladen.</p>
      </div>
    );
  } else if (visibleEntries.length === 0) {
    listSection = (
      <div className="empty" data-testid="empty-state">
        <h2>Noch nichts hinterlegt</h2>
        <p>{renderEmptyText(hasFilters)}</p>
      </div>
    );
  } else {
    listSection = (
      <section className="grid" data-testid="entries-grid">
        {visibleEntries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            onEdit={() => openEdit(entry)}
            onDelete={() => askDelete(entry)}
          />
        ))}
      </section>
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <div className="brand-mark">UZ</div>
            <div className="brand-text">
              <h1>URL-Zentrale</h1>
              <small>Privates Dashboard</small>
            </div>
          </div>
          <div className="user-pill" data-testid="user-pill">
            <span>{auth.email()}</span>
            <span style={{ color: "#3c4754" }}>•</span>
            <button onClick={handleLogout} data-testid="logout-btn">
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <main className="container">
        <section className="counters" aria-label="Zähler">
          <div className="counter gold">
            <div>
              <div className="label">Gesamt</div>
              <div className="value" data-testid="count-total">
                {stats.total}
              </div>
            </div>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#cba35f" strokeWidth="1.5">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
          </div>
          <div className="counter live">
            <div>
              <div className="label">Live</div>
              <div className="value" data-testid="count-live">
                {stats.live}
              </div>
            </div>
            <span className="dot live" style={{ width: 18, height: 18 }} />
          </div>
          <div className="counter work">
            <div>
              <div className="label">In Arbeit</div>
              <div className="value" data-testid="count-work">
                {stats.work}
              </div>
            </div>
            <span className="dot work" style={{ width: 18, height: 18 }} />
          </div>
        </section>

        <section className="sub-counters" aria-label="Hosting-Aufschlüsselung">
          <button
            type="button"
            className={`sub-pill own${hostingFilter === HOSTING.OWN ? " active" : ""}`}
            onClick={() =>
              setHostingFilter(hostingFilter === HOSTING.OWN ? "" : HOSTING.OWN)
            }
            data-testid="sub-own"
            title="Klick: nur Apps mit eigener Domain anzeigen"
          >
            <span className="sub-icon">🌐</span>
            <span className="sub-label">Eigene Domain</span>
            <span className="sub-count">{stats.own}</span>
          </button>
          <button
            type="button"
            className={`sub-pill emergent${hostingFilter === HOSTING.EMERGENT ? " active" : ""}`}
            onClick={() =>
              setHostingFilter(hostingFilter === HOSTING.EMERGENT ? "" : HOSTING.EMERGENT)
            }
            data-testid="sub-emergent"
            title="Klick: nur Emergent-gehostete Apps anzeigen"
          >
            <span className="sub-icon">⚡</span>
            <span className="sub-label">Emergent</span>
            <span className="sub-count">{stats.emergent}</span>
          </button>
          <button
            type="button"
            className={`sub-pill none${hostingFilter === HOSTING.NONE ? " active" : ""}`}
            onClick={() =>
              setHostingFilter(hostingFilter === HOSTING.NONE ? "" : HOSTING.NONE)
            }
            data-testid="sub-none"
            title="Klick: nur Apps ohne URL anzeigen"
          >
            <span className="sub-icon">📝</span>
            <span className="sub-label">Ohne URL</span>
            <span className="sub-count">{stats.noUrl}</span>
          </button>
        </section>

        <section className="toolbar" aria-label="Werkzeuge">
          <div className="search">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Suchen … Name, Beschreibung, Plattform, Notiz"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <select
            className="select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="filter-type"
          >
            <option value="">Alle Typen</option>
            {ENTRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="filter-status"
          >
            <option value="">Alle Status</option>
            {ENTRY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            title="Sortierung"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={hostingFilter}
            onChange={(e) => setHostingFilter(e.target.value)}
            data-testid="filter-hosting"
            title="Hosting filtern"
          >
            {HOSTING_FILTER_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            className="btn btn-ghost"
            onClick={handleExport}
            data-testid="btn-export"
            title="Als JSON-Backup sichern"
          >
            ↓ Backup
          </button>
          <button
            className="btn btn-ghost"
            onClick={handleImportClick}
            data-testid="btn-import"
            title="JSON-Backup laden"
          >
            ↑ Import
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            style={{ display: "none" }}
            onChange={handleImportFile}
            data-testid="import-file"
          />
          <button
            className="btn btn-gold"
            onClick={openNew}
            data-testid="btn-add"
          >
            + Neuer Eintrag
          </button>
        </section>

        {listSection}
      </main>

      {modalOpen && (
        <EntryModal
          initial={editing}
          onClose={closeModal}
          onSave={handleSave}
          types={ENTRY_TYPES}
          statuses={ENTRY_STATUSES}
        />
      )}

      {confirm && (
        <Confirm
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          secondaryLabel={confirm.secondaryLabel}
          danger={confirm.danger}
          onConfirm={confirm.onConfirm}
          onSecondary={confirm.onSecondary}
          onCancel={() => setConfirm(null)}
        />
      )}

      {toast && <Toast message={toast.msg} type={toast.type} />}
    </div>
  );
}
